import { useEffect, useMemo } from 'react'
import { useSelector } from 'react-redux'
import {
  leadListColumns,
  lockedLeadColumns,
} from '@user/components/Leads/LeadList/leadListColumns'
import { arrangeColumns } from '@user/components/xGrid'
import { useSelectCustomFieldData } from '@user/store/customFields'
import { selectAllTargetPests } from '@user/store/targetPests'
import {
  getDefaultGuestUserLeadsColumnVisibility,
  selectGuestUserLeadsColumnOrder,
} from '@user/store/guestUser/slice'
import { useLoadListViewPreferencesOnce } from '@user/store/userPreferences/hooks/useLoadListViewPreferencesOnce'
import { createCustomFieldColumns } from '@user/components/xGrid/atoms/GridCustomToolbar/GridColumnsSelector/createCustomFieldColumns'
import { useAddMissingColumns } from '@user/components/xGrid/valueFormatters/syncNewColumns'
import { useMapViewFeatureEnabled } from '@user/store/tenantFeatures/hooks/useMapViewFeatureEnabled'
import { useD2DKnockEnabled } from '@user/store/tenantFeatures/hooks/useD2DKnockEnabled'

export const useGuestUserLeadsColumns = () => {
  const { customFields } = useSelectCustomFieldData()
  const allTargetPests = useSelector(selectAllTargetPests)
  const userPrefsLoading = useLoadListViewPreferencesOnce()

  const mapViewEnabled = useMapViewFeatureEnabled()
  const d2DKnockEnabled = useD2DKnockEnabled()
  const knockStatusEnabled = mapViewEnabled && d2DKnockEnabled

  const columnOrder = useSelector(selectGuestUserLeadsColumnOrder)
  const [columns, columnVisibility] = useMemo(() => {
    // If no column order is saved, use default visibility but keep all columns available
    if (!columnOrder || columnOrder.length === 0) {
      const defaultVisibility = getDefaultGuestUserLeadsColumnVisibility()
      const allColumns = leadListColumns(
        allTargetPests,
        false,
        knockStatusEnabled,
      )
      const [customColumns, customVisibility] =
        createCustomFieldColumns(customFields)

      return [
        [...allColumns, ...customColumns], // Return ALL columns so they're available in column selector
        { ...defaultVisibility, ...customVisibility }, // But only show the default ones by default
      ]
    }

    return arrangeColumns(
      columnOrder,
      leadListColumns(allTargetPests, false, knockStatusEnabled),
      lockedLeadColumns,
      customFields,
    )
  }, [columnOrder, customFields, allTargetPests, knockStatusEnabled])

  const { syncNewColumnsWithPreferences } = useAddMissingColumns({
    columnOrder,
    columns,
    newlyAddedColumns: [
      'customerReferralLocationCode',
      'employeeReferral',
      'otherReferral',
      ...(knockStatusEnabled ? ['knockStatus', 'knockStatusReason'] : []),
    ],
    preference: 'guestUserDashboardLeads',
  })

  const loading = userPrefsLoading

  useEffect(() => {
    syncNewColumnsWithPreferences()
  }, [columnOrder, syncNewColumnsWithPreferences])

  return { loading, columns, columnVisibility }
}
